import { ArrowLeft, ExternalLink } from 'lucide-react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useGetClaimDetailQuery, useGetClaimFlagsQuery, useGetClaimStatusQuery, useGetClaimTimelineQuery } from '@/services/api/miniTortService';

import { MiniTortActions } from './MiniTortActions';
import { MiniTortFlags } from './MiniTortFlags';
import { MiniTortManualCoverage } from './MiniTortManualCoverage';
import { MiniTortPrerequisites } from './MiniTortPrerequisites';
import { MiniTortStageBadge } from './MiniTortStageBadge';
import { MiniTortStageGuidance } from './MiniTortStageGuidance';
import { MiniTortStageStepper } from './MiniTortStageStepper';
import { MiniTortTasks } from './MiniTortTasks';
import { MiniTortTimeline } from './MiniTortTimeline';
import { ClaimBanners } from './sections/ClaimBanners';
import {
  AccidentDetailsSection,
  AttachmentsSection,
  ClaimantInfoSection,
  CoverageStatusSection,
  EligibilitySection,
  PoliceReportSection,
  RequestedMaterialsSection,
} from './sections/ClaimInfoSections';
import { MiniTortStage, TERMINAL_STAGES } from './types';
import { formatDateWithRelative } from './utils';

export const MiniTortClaimDetail: React.FC = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const resolvedClaimId = claimId ?? '';
  const { data: claim, isLoading, error } = useGetClaimDetailQuery(resolvedClaimId, { skip: !resolvedClaimId });
  const { data: pipelineStatus } = useGetClaimStatusQuery(resolvedClaimId, { skip: !resolvedClaimId });
  const { data: timeline } = useGetClaimTimelineQuery(resolvedClaimId, { skip: !resolvedClaimId });
  const { data: flags } = useGetClaimFlagsQuery(resolvedClaimId, { skip: !resolvedClaimId });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
          <span className="text-sm text-zinc-500">Loading claim...</span>
        </div>
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="px-10 py-8">
        <Link to="/mini-tort/claims" className="mb-4 flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300">
          <ArrowLeft className="h-4 w-4" /> Back to Claims
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Failed to load claim details</p>
        </div>
      </div>
    );
  }

  const days = Math.floor((new Date().getTime() - new Date(claim.stage_entered_at).getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = days > 14 && !TERMINAL_STAGES.has(claim.stage);
  const { submission } = claim;

  return (
    <div className="flex flex-col gap-4 px-4 py-6 sm:gap-6 sm:px-6 lg:px-10 lg:py-8">
      {/* Header */}
      <div>
        <Link to="/mini-tort/claims" className="mb-3 flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300">
          <ArrowLeft className="h-4 w-4" /> Back to Claims
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white">
            {submission ? `${submission.claimant_first_name} ${submission.claimant_last_name}` : `Claim ${claim.id.slice(0, 8)}`}
          </h1>
          <MiniTortStageBadge stage={claim.stage} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-500 dark:text-zinc-400">
          <span>ID: {claim.id.slice(0, 12)}...</span>
          {submission?.accident_date && (
            <span>Accident: {formatDateWithRelative(submission.accident_date)}</span>
          )}
          <span>
            In stage for{' '}
            <span className={cn('font-medium', isOverdue && 'text-red-600 dark:text-red-400')}>{days} day{days !== 1 ? 's' : ''}</span>
          </span>
          {claim.matter_id && (
            <Link to={`/matters/${claim.matter_id}`} className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400">
              <ExternalLink className="h-3.5 w-3.5" /> View Matter
            </Link>
          )}
        </div>
      </div>

      <ClaimBanners claim={claim} submission={submission} />

      <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
        <MiniTortStageStepper currentStage={claim.stage} timeline={timeline} />
      </div>

      <MiniTortStageGuidance claim={claim} status={pipelineStatus} />

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {submission && <ClaimantInfoSection claim={claim} submission={submission} />}
          {submission && <AccidentDetailsSection claim={claim} submission={submission} />}
          <CoverageStatusSection claim={claim} />
          <MiniTortManualCoverage claim={claim} flags={flags ?? []} />
          <PoliceReportSection claim={claim} submission={submission} />
          {submission && submission.attachments && submission.attachments.length > 0 && (
            <AttachmentsSection attachments={submission.attachments} />
          )}
          {claim.missing_item_prompt && claim.stage === MiniTortStage.NEED_ADDITIONAL_MATERIALS && (
            <RequestedMaterialsSection prompt={claim.missing_item_prompt} />
          )}
          {submission && <EligibilitySection submission={submission} />}
          <MiniTortTasks key={claim.stage} matterId={claim.matter_id} />
          <MiniTortTimeline claimId={claim.id} />
        </div>

        <div className="space-y-6">
          <MiniTortFlags claimId={claim.id} />
          <MiniTortActions claim={claim} status={pipelineStatus} />
          {pipelineStatus && <MiniTortPrerequisites prerequisites={pipelineStatus.prerequisites} />}
        </div>
      </div>
    </div>
  );
};
